import React, { useState, useMemo, useRef } from "react";
import { useNavigationEvent } from "navigation-react";
import {
  faSpinner,
  faTimes,
  faPrint,
  faCalendar,
  faClose,
  faCheckCircle,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useSWR from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import MessageModal from "./MessageModal";
import ReceiptPrinterEncoder from "@point-of-sale/receipt-printer-encoder";

// Fetcher for orders API
const ordersFetcher = async (url) => {
  try {
    const response = await fetch(url, {
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

export default function DailyReport() {
  const { stateNavigator } = useNavigationEvent();
  const printRef = useRef();
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isPrinting, setIsPrinting] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printerIP, setPrinterIP] = useState("192.168.1.233");
  const [printerPort, setPrinterPort] = useState(9100);

  // Load printer settings
  React.useEffect(() => {
    const savedIP = localStorage.getItem("printer_ip");
    const savedPort = localStorage.getItem("printer_port");
    if (savedIP) setPrinterIP(savedIP);
    if (savedPort) setPrinterPort(parseInt(savedPort));
  }, []);

  // Format date for API
  const formatDateForAPI = (date) => {
    if (!date || isNaN(date.getTime())) return "";
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getApiUrl = () => {
    const startDateParam = formatDateForAPI(startDate);
    const endDateParam = formatDateForAPI(endDate);

    // If same date, use the original single date parameter
    if (startDateParam === endDateParam) {
      return `${process.env.REACT_APP_BASE_URL}/api/orders?date=${startDateParam}`;
    }

    // For date range, use from_date and to_date (matching PHP backend)
    return `${process.env.REACT_APP_BASE_URL}/api/orders?from_date=${startDateParam}&to_date=${endDateParam}`;
  };

  const {
    data: ordersResponse,
    isLoading,
    error,
    mutate,
  } = useSWR(getApiUrl(), ordersFetcher, {
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    refreshInterval: 60000,
  });

  const allOrders = ordersResponse?.data || [];

  // Helper to calculate subtotal from items
  const calculateSubtotal = (items) => {
    if (!items || !items.length) return 0;
    return items.reduce((sum, item) => {
      const qty = item.quantity || 0;
      const price = item.price || 0;
      return sum + price * qty;
    }, 0);
  };

  // Calculate food refund amount
  const calculateFoodRefundAmount = (order) => {
    if (!order.items || !order.items.length) return 0;
    let foodRefundedAmount = 0;
    order.items.forEach((item) => {
      if (item.status === "voided") return;
      const refundedQty = item.refunded_quantity || 0;
      if (refundedQty > 0) {
        foodRefundedAmount += item.price * refundedQty;
      }
    });
    return foodRefundedAmount;
  };

  // Calculate current subtotal (with discount applied)
  const calculateCurrentSubtotal = (order) => {
    if (!order.items || !order.items.length) return 0;
    return order.items.reduce((sum, item) => {
      if (item.status === "voided") return sum;
      const remainingQty = item.quantity - (item.refunded_quantity || 0);
      return sum + item.price * remainingQty;
    }, 0);
  };

  // Calculate current service charge
  const calculateCurrentServiceCharge = (order) => {
    const subtotal = calculateCurrentSubtotal(order);
    const scPercentage =
      order.service_charge_percentage ||
      (order.order_type === "dine-in" ? 5 : 0);
    return subtotal * (scPercentage / 100);
  };

  // Calculate total refunded amount
  const calculateTotalRefunded = (orders) => {
    let total = 0;
    orders.forEach((order) => {
      const foodRefund = calculateFoodRefundAmount(order);
      let serviceChargeRefund = 0;
      if (order.order_type === "dine-in") {
        const scPercentage = order.service_charge_percentage || 5;
        serviceChargeRefund = foodRefund * (scPercentage / 100);
      }
      total += foodRefund + serviceChargeRefund;
    });
    return total;
  };

  // Calculate total voided amount
  const calculateTotalVoided = (orders) => {
    let total = 0;
    orders.forEach((order) => {
      if (order.items) {
        order.items.forEach((item) => {
          if (item.status === "voided") {
            total += item.price * item.quantity;
          }
        });
      }
    });
    return total;
  };

  // Calculate cashier summary
  const cashierSummary = useMemo(() => {
    if (!allOrders.length) {
      return [];
    }

    const paidOrders = allOrders.filter((order) => order.status === "paid");
    const cashierMap = new Map();

    paidOrders.forEach((order) => {
      const cashierName = order.cashier_name || order.cashier || "Unknown";

      if (!cashierMap.has(cashierName)) {
        cashierMap.set(cashierName, {
          name: cashierName,
          orderCount: 0,
          totalSales: 0,
          totalRevenue: 0,
          totalServiceCharge: 0,
          totalDiscount: 0,
          cashTotal: 0,
          cardTotal: 0,
          mobileMoneyTotal: 0,
          bankTransferTotal: 0,
        });
      }

      const cashierData = cashierMap.get(cashierName);

      // Calculate order totals
      const subtotal = order.menu_subtotal || calculateSubtotal(order.items);
      const discount = order.discount || 0;
      const discountedSubtotal =
        order.discounted_subtotal || subtotal - discount;

      const scPercentage =
        order.service_charge_percentage ||
        (order.order_type === "dine-in" ? 5 : 0);
      const scAmount =
        order.service_charge_amount ||
        discountedSubtotal * (scPercentage / 100);

      const vatAmount = order.vat || discountedSubtotal * 0.15;
      const nhilAmount = order.nhil || discountedSubtotal * 0.025;
      const getfundAmount = order.getfund || discountedSubtotal * 0.025;
      const tourismAmount = order.tourism || discountedSubtotal * 0.01;

      const total =
        discountedSubtotal +
        scAmount +
        vatAmount +
        nhilAmount +
        getfundAmount +
        tourismAmount;

      cashierData.orderCount += 1;
      cashierData.totalSales += discountedSubtotal;
      cashierData.totalRevenue += total;
      cashierData.totalServiceCharge += scAmount;
      cashierData.totalDiscount += discount;

      // Payment method totals
      const paymentMethod = order.payment_method;
      const paymentAmount = order.payment_amount || total;

      switch (paymentMethod) {
        case "cash":
          cashierData.cashTotal += paymentAmount;
          break;
        case "card":
          cashierData.cardTotal += paymentAmount;
          break;
        case "mobile_money":
          cashierData.mobileMoneyTotal += paymentAmount;
          break;
        case "bank_transfer":
          cashierData.bankTransferTotal += paymentAmount;
          break;
        default:
          break;
      }
    });

    return Array.from(cashierMap.values());
  }, [allOrders]);

  // Calculate report data with Cash Register statistics
  const reportData = useMemo(() => {
    if (!allOrders.length) {
      return {
        firstOrder: null,
        lastOrder: null,
        firstInvoice: null,
        lastInvoice: null,
        numberOfCustomers: 0,
        numberOfInvoices: 0,
        grandTotal: 0,
        nhil: 0,
        getfund: 0,
        tourismLevy: 0,
        vat: 0,
        service: 0,
        discount: 0,
        netTotal: 0,
        noSales: 0,
        subTotal: 0,
        averageCustomer: 0,
        averageCheck: 0,
        netSalesWithoutTaxes: 0,
        netSales: 0,
        dineInOrders: 0,
        takeawayOrders: 0,
        deliveryOrders: 0,
        totalGuests: 0,
        totalRefunded: 0,
        totalVoided: 0,
        totalDiscount: 0,
        cashPayments: 0,
        cardPayments: 0,
        mobileMoneyPayments: 0,
        bankTransferPayments: 0,
        paymentSummary: {
          cash: 0,
          card: 0,
          mobile_money: 0,
          bank_transfer: 0,
          total: 0,
        },
        paidOrdersCount: 0,
        totalOrders: 0,
      };
    }

    // Sort orders by created date
    const sortedOrders = [...allOrders].sort(
      (a, b) => new Date(a.created) - new Date(b.created),
    );

    const firstOrder = sortedOrders[0];
    const lastOrder = sortedOrders[sortedOrders.length - 1];

    // Get paid orders only
    const paidOrders = allOrders.filter((order) => order.status === "paid");

    // Calculate invoice numbers (using order IDs)
    const sortedIds = sortedOrders.map((o) => o.id).sort((a, b) => a - b);
    const firstInvoice = sortedIds.length > 0 ? sortedIds[0] : null;
    const lastInvoice =
      sortedIds.length > 0 ? sortedIds[sortedIds.length - 1] : null;

    // Calculate number of customers (unique guest names)
    const uniqueGuests = new Set();
    allOrders.forEach((order) => {
      if (order.guest_name && order.guest_name !== "Guest") {
        uniqueGuests.add(order.guest_name);
      }
    });
    const numberOfCustomers = uniqueGuests.size || allOrders.length;

    const numberOfInvoices = allOrders.length;

    // Calculate totals from paid orders
    let grandTotal = 0;
    let subTotal = 0;
    let totalServiceCharge = 0;
    let totalVat = 0;
    let totalNhil = 0;
    let totalGetfund = 0;
    let totalTourism = 0;
    let totalDiscount = 0;

    // Cash Register Statistics
    let dineInOrders = 0;
    let takeawayOrders = 0;
    let deliveryOrders = 0;
    let totalGuests = 0;
    let cashTotal = 0;
    let cardTotal = 0;
    let mobileMoneyTotal = 0;
    let bankTransferTotal = 0;
    let paidOrdersCount = paidOrders.length;
    let totalOrders = allOrders.length;

    paidOrders.forEach((order) => {
      // Use stored values if available, otherwise calculate
      const subtotal = order.menu_subtotal || calculateSubtotal(order.items);
      const discount = order.discount || 0;
      const discountedSubtotal =
        order.discounted_subtotal || subtotal - discount;

      // Use stored tax values if available
      const vatAmount = order.vat || discountedSubtotal * 0.15;
      const nhilAmount = order.nhil || discountedSubtotal * 0.025;
      const getfundAmount = order.getfund || discountedSubtotal * 0.025;
      const tourismAmount = order.tourism || discountedSubtotal * 0.01;

      // Service charge
      const scPercentage =
        order.service_charge_percentage ||
        (order.order_type === "dine-in" ? 5 : 0);
      const scAmount =
        order.service_charge_amount ||
        discountedSubtotal * (scPercentage / 100);

      const total =
        discountedSubtotal +
        scAmount +
        vatAmount +
        nhilAmount +
        getfundAmount +
        tourismAmount;

      subTotal += subtotal;
      totalServiceCharge += scAmount;
      totalVat += vatAmount;
      totalNhil += nhilAmount;
      totalGetfund += getfundAmount;
      totalTourism += tourismAmount;
      totalDiscount += discount;
      grandTotal += total;

      // Order type counts
      switch (order.order_type) {
        case "dine-in":
          dineInOrders++;
          if (order.number_of_people) {
            totalGuests += order.number_of_people;
          }
          break;
        case "takeaway":
          takeawayOrders++;
          break;
        case "delivery":
          deliveryOrders++;
          break;
        default:
          break;
      }

      // Payment method totals
      const paymentMethod = order.payment_method;
      const paymentAmount = order.payment_amount || total;

      switch (paymentMethod) {
        case "cash":
          cashTotal += paymentAmount;
          break;
        case "card":
          cardTotal += paymentAmount;
          break;
        case "mobile_money":
          mobileMoneyTotal += paymentAmount;
          break;
        case "bank_transfer":
          bankTransferTotal += paymentAmount;
          break;
        default:
          break;
      }
    });

    // Calculate refunded and voided amounts
    const totalRefunded = calculateTotalRefunded(paidOrders);
    const totalVoided = calculateTotalVoided(allOrders);

    // Calculate net totals
    const netTotal = grandTotal;
    const netSalesWithoutTaxes = subTotal + totalServiceCharge - totalDiscount;
    const netSales = grandTotal;

    // No sales (orders without items or zero total)
    const noSales = allOrders.filter((order) => {
      const total = parseFloat(order.total || 0);
      const items = order.items || [];
      return total === 0 || items.length === 0;
    }).length;

    const averageCustomer =
      numberOfCustomers > 0 ? grandTotal / numberOfCustomers : 0;
    const averageCheck =
      numberOfInvoices > 0 ? grandTotal / numberOfInvoices : 0;

    return {
      firstOrder: firstOrder ? new Date(firstOrder.created) : null,
      lastOrder: lastOrder ? new Date(lastOrder.created) : null,
      firstInvoice,
      lastInvoice,
      numberOfCustomers,
      numberOfInvoices,
      grandTotal,
      nhil: totalNhil,
      getfund: totalGetfund,
      tourismLevy: totalTourism,
      vat: totalVat,
      service: totalServiceCharge,
      discount: totalDiscount,
      netTotal,
      noSales,
      subTotal,
      averageCustomer,
      averageCheck,
      netSalesWithoutTaxes,
      netSales,
      dineInOrders,
      takeawayOrders,
      deliveryOrders,
      totalGuests,
      totalRefunded,
      totalVoided,
      totalDiscount,
      cashPayments: cashTotal,
      cardPayments: cardTotal,
      mobileMoneyPayments: mobileMoneyTotal,
      bankTransferPayments: bankTransferTotal,
      paymentSummary: {
        cash: cashTotal,
        card: cardTotal,
        mobile_money: mobileMoneyTotal,
        bank_transfer: bankTransferTotal,
        total: grandTotal,
      },
      paidOrdersCount,
      totalOrders,
    };
  }, [allOrders]);

  // Generate thermal receipt for report with Cash Register stats
  const generateThermalReport = (
    reportData,
    reportStartDate,
    reportEndDate,
    cashierSummary,
  ) => {
    const encoder = new ReceiptPrinterEncoder({
      language: "esc-pos",
      type: "epson",
    });

    const formatCurrency = (amount) =>
      `GHC${parseFloat(amount || 0).toFixed(2)}`;
    const LINE_WIDTH = 42;

    const COL_WIDTHS = {
      label: 28,
      value: 14,
    };

    const padRight = (str, len) => {
      const s = String(str || "");
      return s.length > len ? s.substring(0, len) : s.padEnd(len);
    };

    const padLeft = (str, len) => {
      const s = String(str || "");
      return s.length > len ? s.substring(0, len) : s.padStart(len);
    };

    const formatTotalLine = (label, amount) => {
      return `${padRight(label, COL_WIDTHS.label)}${padLeft(amount, COL_WIDTHS.value)}`;
    };

    const line = "=".repeat(LINE_WIDTH);
    const dashLine = "-".repeat(LINE_WIDTH);

    const startDateStr = reportStartDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const endDateStr = reportEndDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const dateRangeStr =
      startDateStr === endDateStr
        ? startDateStr
        : `${startDateStr} - ${endDateStr}`;

    encoder
      .initialize()
      .align("center")
      .size(2)
      .bold(true)
      .line("THE HEADQUARTERS EATERY")
      .size(1)
      .bold(false)
      .line("21st Otele Avenue, Accra")
      .line("TIN: P0013030590")
      .line(line)
      .bold(true)
      .line("DAILY REPORT")
      .bold(false)
      .line(`${dateRangeStr}`)
      .line(line)
      .newline()
      .align("left")
      .line(
        `First Order:  ${reportData.firstOrder ? new Date(reportData.firstOrder).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "N/A"}`,
      )
      .line(
        `Last Order:   ${reportData.lastOrder ? new Date(reportData.lastOrder).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "N/A"}`,
      )
      .line(`First Invoice: ${reportData.firstInvoice || "N/A"}`)
      .line(`Last Invoice:  ${reportData.lastInvoice || "N/A"}`)
      .line(`Customers:     ${reportData.numberOfCustomers}`)
      .line(`Invoices:      ${reportData.numberOfInvoices}`)
      .line(`Paid Orders:   ${reportData.paidOrdersCount}`)
      .line(`Total Orders:  ${reportData.totalOrders}`)
      .newline()
      .align("center")
      .line(line)
      .bold(true)
      .line("ORDER TYPE BREAKDOWN")
      .bold(false)
      .align("left")
      .line(formatTotalLine("Dine In:", String(reportData.dineInOrders)))
      .line(formatTotalLine("Takeaway:", String(reportData.takeawayOrders)))
      .line(formatTotalLine("Delivery:", String(reportData.deliveryOrders)))
      .line(formatTotalLine("Total Guests:", String(reportData.totalGuests)))
      .newline()
      .align("center")
      .line(line)
      .bold(true)
      .line("CASHIER SUMMARY")
      .bold(false)
      .align("left");

    // Add cashier summary data
    if (cashierSummary && cashierSummary.length > 0) {
      cashierSummary.forEach((cashier) => {
        encoder
          .line(`-- ${cashier.name} --`)
          .line(formatTotalLine("Orders:", String(cashier.orderCount)))
          .line(
            formatTotalLine("Total Sales:", formatCurrency(cashier.totalSales)),
          )
          .line(
            formatTotalLine(
              "Total Revenue:",
              formatCurrency(cashier.totalRevenue),
            ),
          )
          .line(
            formatTotalLine(
              "Service Charge:",
              formatCurrency(cashier.totalServiceCharge),
            ),
          )
          .line(
            formatTotalLine("Discount:", formatCurrency(cashier.totalDiscount)),
          )
          .line(formatTotalLine("Cash:", formatCurrency(cashier.cashTotal)))
          .line(formatTotalLine("Card:", formatCurrency(cashier.cardTotal)))
          .line(
            formatTotalLine(
              "Mobile Money:",
              formatCurrency(cashier.mobileMoneyTotal),
            ),
          )
          .line(
            formatTotalLine(
              "Bank Transfer:",
              formatCurrency(cashier.bankTransferTotal),
            ),
          )
          .newline();
      });
    } else {
      encoder.line("No cashier data available");
    }

    encoder
      .align("center")
      .line(line)
      .bold(true)
      .line("FINANCIAL SUMMARY")
      .bold(false)
      .align("left")
      .line(
        formatTotalLine("Grand Total:", formatCurrency(reportData.grandTotal)),
      )
      .line(formatTotalLine("Sub Total:", formatCurrency(reportData.subTotal)))
      .line(formatTotalLine("Discount:", formatCurrency(reportData.discount)))
      .line(formatTotalLine("Service:", formatCurrency(reportData.service)))
      .line(formatTotalLine("15% VAT:", formatCurrency(reportData.vat)))
      .line(formatTotalLine("2.5% NHIL:", formatCurrency(reportData.nhil)))
      .line(
        formatTotalLine("2.5% GetFund:", formatCurrency(reportData.getfund)),
      )
      .line(
        formatTotalLine(
          "Tourism Levy:",
          formatCurrency(reportData.tourismLevy),
        ),
      )
      .line(
        formatTotalLine("Refunded:", formatCurrency(reportData.totalRefunded)),
      )
      .line(formatTotalLine("Voided:", formatCurrency(reportData.totalVoided)))
      .newline()
      .align("center")
      .line(line)
      .bold(true)
      .line("PAYMENT SUMMARY")
      .bold(false)
      .align("left")
      .line(
        formatTotalLine(
          "Cash:",
          formatCurrency(reportData.paymentSummary.cash),
        ),
      )
      .line(
        formatTotalLine(
          "Card:",
          formatCurrency(reportData.paymentSummary.card),
        ),
      )
      .line(
        formatTotalLine(
          "Mobile Money:",
          formatCurrency(reportData.paymentSummary.mobile_money),
        ),
      )
      .line(
        formatTotalLine(
          "Bank Transfer:",
          formatCurrency(reportData.paymentSummary.bank_transfer),
        ),
      )
      .line(
        formatTotalLine(
          "Total:",
          formatCurrency(reportData.paymentSummary.total),
        ),
      )
      .newline()
      .align("center")
      .line(line)
      .bold(true)
      .line("NET TOTALS")
      .bold(false)
      .align("left")
      .line(
        formatTotalLine(
          "Net Sales Without Taxes:",
          formatCurrency(reportData.netSalesWithoutTaxes),
        ),
      )
      .line(formatTotalLine("Net Total:", formatCurrency(reportData.netTotal)))
      .line(
        formatTotalLine(
          "Average Customer:",
          formatCurrency(reportData.averageCustomer),
        ),
      )
      .line(
        formatTotalLine(
          "Average Check:",
          formatCurrency(reportData.averageCheck),
        ),
      )
      .line(formatTotalLine("No Sales #:", String(reportData.noSales)))
      .newline()
      .align("center")
      .line(line)
      .bold(true)
      .line("NET SALES")
      .bold(false)
      .align("center")
      .size(2)
      .bold(true)
      .line(`GHC${formatCurrency(reportData.netSales)}`)
      .bold(false)
      .size(1)
      .newline()
      .align("center")
      .line(dashLine)
      .line("End of Report")
      .newline()
      .line(line)
      .newline()
      .newline()
      .cut("partial");

    return encoder.encode();
  };

  // Thermal printer print function
  const handleThermalPrint = async () => {
    if (printing) return;
    setPrinting(true);
    setErrorMessage("");

    try {
      localStorage.setItem("printer_ip", printerIP);
      localStorage.setItem("printer_port", printerPort.toString());

      const data = generateThermalReport(
        reportData,
        startDate,
        endDate,
        cashierSummary,
      );
      const dataArray = Array.from(data);

      const response = await fetch("http://localhost:3001/print", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ip: printerIP,
          port: printerPort,
          data: dataArray,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to print report");
      }

      setErrorMessage("✅ Report printed successfully!");
      setModalError(true);
      setTimeout(() => setModalError(false), 3000);
    } catch (error) {
      console.error("Print failed:", error);
      setErrorMessage(`Print failed: ${error.message}`);
      setModalError(true);
    } finally {
      setPrinting(false);
    }
  };

  // Window print function (PDF) - Optimized for A4 with font-size 16
  const handleWindowPrint = () => {
    setIsPrinting(true);
    setTimeout(() => {
      const printContent = printRef.current;
      if (printContent) {
        const win = window.open("", "_blank", "width=900,height=700");
        if (win) {
          const startDateStr = startDate.toLocaleDateString("en-GB", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          });
          const endDateStr = endDate.toLocaleDateString("en-GB", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          });
          const dateRangeStr =
            startDateStr === endDateStr
              ? startDateStr
              : `${startDateStr} - ${endDateStr}`;

          win.document.write(`
            <html>
              <head>
                <title>Daily Report - ${dateRangeStr}</title>
                <style>
                  * { margin: 0; padding: 0; box-sizing: border-box; }
                  body { 
                    font-family: 'Segoe UI', Arial, sans-serif; 
                    padding: 30px; 
                    background: white;
                    color: #1F2937;
                    font-size: 16px;
                    line-height: 1.6;
                  }
                  .report-container {
                    max-width: 1000px;
                    margin: 0 auto;
                    padding: 20px;
                  }
                  .header {
                    text-align: center;
                    margin-bottom: 30px;
                    border-bottom: 2px solid #1F2937;
                    padding-bottom: 20px;
                  }
                  .header h1 {
                    font-size: 32px;
                    font-weight: 700;
                    margin-bottom: 8px;
                    color: #1F2937;
                    letter-spacing: 2px;
                  }
                  .header h2 {
                    font-size: 24px;
                    font-weight: 600;
                    margin-bottom: 6px;
                    color: #FF6B35;
                  }
                  .header p {
                    font-size: 16px;
                    color: #6B7280;
                    margin: 2px 0;
                  }
                  .header .date {
                    font-size: 20px;
                    font-weight: 600;
                    color: #1F2937;
                    margin-top: 8px;
                  }
                  .section {
                    margin-bottom: 24px;
                    page-break-inside: avoid;
                  }
                  .section-title {
                    font-size: 20px;
                    font-weight: 700;
                    border-bottom: 2px solid #E5E7EB;
                    padding-bottom: 10px;
                    margin-bottom: 16px;
                    display: flex;
                    align-items: center;
                    color: #1F2937;
                  }
                  .summary-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 12px 20px;
                    margin-bottom: 20px;
                    padding: 16px 20px;
                    background-color: #F8FAFC;
                    border-radius: 8px;
                    border: 1px solid #E5E7EB;
                  }
                  .summary-item {
                    display: flex;
                    justify-content: space-between;
                    padding: 4px 0;
                    font-size: 16px;
                    align-items: center;
                  }
                  .summary-label {
                    color: #6B7280;
                    font-weight: 500;
                  }
                  .summary-value {
                    font-weight: 600;
                    color: #1F2937;
                  }
                  .cash-register-stats {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 8px 20px;
                    padding: 16px 20px;
                    background: #F0FDF4;
                    border-radius: 8px;
                    border: 2px solid #86EFAC;
                  }
                  .stat-item {
                    display: flex;
                    justify-content: space-between;
                    padding: 4px 0;
                    font-size: 16px;
                    align-items: center;
                  }
                  .stat-label {
                    color: #065F46;
                    font-weight: 500;
                  }
                  .stat-value {
                    font-weight: 600;
                    color: #1F2937;
                  }
                  .cashier-summary {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 12px;
                    padding: 16px 20px;
                    background: #EFF6FF;
                    border-radius: 8px;
                    border: 2px solid #93C5FD;
                  }
                  .cashier-header {
                    display: grid;
                    grid-template-columns: 1fr 0.5fr 1fr 1fr 0.8fr;
                    gap: 8px;
                    padding: 8px 12px;
                    font-weight: 700;
                    color: #1F2937;
                    border-bottom: 2px solid #93C5FD;
                    font-size: 15px;
                  }
                  .cashier-item {
                    display: grid;
                    grid-template-columns: 1fr 0.5fr 1fr 1fr 0.8fr;
                    gap: 8px;
                    padding: 8px 12px;
                    background: white;
                    border-radius: 6px;
                    border: 1px solid #E5E7EB;
                    font-size: 14px;
                    align-items: center;
                  }
                  .cashier-name {
                    font-weight: 700;
                    color: #1F2937;
                  }
                  .no-data-message {
                    text-align: center;
                    padding: 20px;
                    color: #6B7280;
                    font-size: 16px;
                  }
                  .financial-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 6px 24px;
                    padding: 12px 16px;
                    background: #FFFFFF;
                    border-radius: 8px;
                    border: 1px solid #E5E7EB;
                  }
                  .financial-item {
                    display: flex;
                    justify-content: space-between;
                    padding: 4px 0;
                    font-size: 16px;
                    border-bottom: 1px solid #F3F4F6;
                    align-items: center;
                  }
                  .financial-item:last-child {
                    border-bottom: none;
                  }
                  .net-totals {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 6px 24px;
                    padding: 12px 16px;
                    background: #F8FAFC;
                    border-radius: 8px;
                    border: 1px solid #E5E7EB;
                  }
                  .net-item {
                    display: flex;
                    justify-content: space-between;
                    padding: 4px 0;
                    font-size: 16px;
                    align-items: center;
                  }
                  .payment-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 6px 20px;
                    padding: 16px 20px;
                    background: #F8FAFC;
                    border-radius: 8px;
                    border: 1px solid #E5E7EB;
                  }
                  .payment-item {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 4px 0;
                    font-size: 16px;
                  }
                  .payment-total {
                    display: flex;
                    justify-content: space-between;
                    padding: 8px 0;
                    font-size: 18px;
                    font-weight: 700;
                    border-top: 2px solid #E5E7EB;
                    margin-top: 4px;
                    grid-column: 1 / -1;
                  }
                  .net-sales-total {
                    display: flex;
                    justify-content: space-between;
                    padding: 16px 24px;
                    background-color: #FEF3C7;
                    border-radius: 8px;
                    font-size: 22px;
                    font-weight: 700;
                    margin-top: 12px;
                    border: 2px solid #F59E0B;
                  }
                  .net-sales-amount {
                    color: #FF6B35;
                    font-size: 28px;
                  }
                  .amount {
                    font-weight: 700;
                    color: #1F2937;
                  }
                  .amount-large {
                    font-weight: 700;
                    color: #FF6B35;
                    font-size: 22px;
                  }
                  .footer {
                    text-align: center;
                    margin-top: 30px;
                    padding-top: 20px;
                    border-top: 1px solid #E5E7EB;
                    font-size: 16px;
                    color: #6B7280;
                  }
                  .footer p {
                    margin: 2px 0;
                  }
                  .refunded { color: #DC2626; }
                  .voided { color: #DC2626; }
                  .discount { color: #DC2626; }
                  .text-right { text-align: right; }
                  .text-center { text-align: center; }
                  .fw-bold { font-weight: 700; }
                  .mt-8 { margin-top: 8px; }
                  .mb-8 { margin-bottom: 8px; }
                  
                  @media print {
                    body { padding: 20px; }
                    .no-print { display: none !important; }
                    .section { page-break-inside: avoid; }
                  }
                  
                  @media (max-width: 768px) {
                    .summary-grid,
                    .cash-register-stats,
                    .financial-grid,
                    .net-totals,
                    .payment-grid {
                      grid-template-columns: 1fr;
                      gap: 4px;
                    }
                    .header h1 { font-size: 24px; }
                    .header h2 { font-size: 20px; }
                  }
                </style>
              </head>
              <body>
                <div class="report-container">
                  <div class="header">
                    <h1>DAILY REPORT</h1>
                    <h2>THE HEADQUARTERS EATERY</h2>
                    <p>21d Otele Avenue, Accra</p>
                    <p>TIN: P0013030590</p>
                    <div class="date">${dateRangeStr}</div>
                  </div>

                  ${printContent.innerHTML}

                  <div class="footer">
                    <p>Generated on: ${new Date().toLocaleString("en-GB")}</p>
                    <p>VAT/TIN: P0013030590</p>
                    <p>www.theheadquarters.com</p>
                  </div>
                </div>
                <script>
                  window.onload = function() {
                    window.print();
                    window.onafterprint = function() {
                      window.close();
                    };
                  }
                <\/script>
              </body>
            </html>
          `);
          win.document.close();
        }
      }
      setIsPrinting(false);
    }, 500);
  };

  const handleStartDateChange = (e) => {
    const newDate = new Date(e.target.value);
    newDate.setHours(0, 0, 0, 0);
    setStartDate(newDate);
  };

  const handleEndDateChange = (e) => {
    const newDate = new Date(e.target.value);
    newDate.setHours(23, 59, 59, 999);
    setEndDate(newDate);
  };

  const formatCurrency = (amount) => {
    return amount ? amount.toFixed(2) : "0.00";
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  if (isLoading && !ordersResponse) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.loadingContainer}>
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                style={{ fontSize: "64px", color: "#FF6B35" }}
              />
              <p style={styles.loadingText}>Loading report...</p>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  if (error && !ordersResponse) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.errorContainer}>
              <FontAwesomeIcon
                icon={faTimes}
                style={{ fontSize: "64px", color: "#d32f2f" }}
              />
              <h3 style={styles.errorTitle}>Error Loading Report</h3>
              <p style={styles.errorSubtitle}>
                Unable to connect to the API. Please check your connection.
              </p>
              <button style={styles.retryButton} onClick={() => mutate()}>
                <FontAwesomeIcon
                  icon={faTimes}
                  style={{ marginRight: "12px" }}
                />
                Retry
              </button>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div style={styles.container}>
          {/* Header */}
          <div style={styles.headerSection}>
            <div>
              <h1 style={styles.title}>Daily Report</h1>
            </div>
            <div style={styles.filterControls}>
              <div style={styles.dateWrapper}>
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  style={{ color: "#FF6B35", fontSize: "18px" }}
                />
                <label style={styles.dateLabel}>From:</label>
                <input
                  type="date"
                  style={styles.dateInput}
                  value={startDate.toISOString().split("T")[0]}
                  onChange={handleStartDateChange}
                />
              </div>
              <div style={styles.dateWrapper}>
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  style={{ color: "#FF6B35", fontSize: "18px" }}
                />
                <label style={styles.dateLabel}>To:</label>
                <input
                  type="date"
                  style={styles.dateInput}
                  value={endDate.toISOString().split("T")[0]}
                  onChange={handleEndDateChange}
                />
              </div>
              <button
                style={styles.printButton}
                onClick={handleWindowPrint}
                disabled={isPrinting}
              >
                <FontAwesomeIcon icon={faPrint} />{" "}
                {isPrinting ? "Preparing..." : "Print PDF"}
              </button>
              <button
                style={styles.thermalPrintButton}
                onClick={handleThermalPrint}
                disabled={printing}
              >
                {printing ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faPrint} />
                )}{" "}
                {printing ? "Printing..." : "Thermal Print"}
              </button>
            </div>
          </div>

          {/* Report Content */}
          <div ref={printRef} style={styles.reportContent}>
            {/* Summary Grid - First Order, Last Order, Invoices */}
            <div style={styles.summaryGrid}>
              <div style={styles.summaryItem}>
                <span style={styles.summaryLabel}>First Order:</span>
                <span style={styles.summaryValue}>
                  {formatDate(reportData.firstOrder)}
                </span>
              </div>
              <div style={styles.summaryItem}>
                <span style={styles.summaryLabel}>Last Order:</span>
                <span style={styles.summaryValue}>
                  {formatDate(reportData.lastOrder)}
                </span>
              </div>
              <div style={styles.summaryItem}>
                <span style={styles.summaryLabel}>First Invoice:</span>
                <span style={styles.summaryValue}>
                  {reportData.firstInvoice || "-"}
                </span>
              </div>
              <div style={styles.summaryItem}>
                <span style={styles.summaryLabel}>Last Invoice:</span>
                <span style={styles.summaryValue}>
                  {reportData.lastInvoice || "-"}
                </span>
              </div>
              <div style={styles.summaryItem}>
                <span style={styles.summaryLabel}>Number of Customers:</span>
                <span style={styles.summaryValue}>
                  {reportData.numberOfCustomers}
                </span>
              </div>
              <div style={styles.summaryItem}>
                <span style={styles.summaryLabel}>Number of Invoices:</span>
                <span style={styles.summaryValue}>
                  {reportData.numberOfInvoices}
                </span>
              </div>
            </div>

            {/* Cash Register Statistics Section */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Order Statistics</h3>
              <div style={styles.cashRegisterStats}>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>Dine In:</span>
                  <span style={styles.statValue}>
                    {reportData.dineInOrders}
                  </span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>Takeaway:</span>
                  <span style={styles.statValue}>
                    {reportData.takeawayOrders}
                  </span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>Delivery:</span>
                  <span style={styles.statValue}>
                    {reportData.deliveryOrders}
                  </span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>Total Guests:</span>
                  <span style={styles.statValue}>{reportData.totalGuests}</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>Paid Orders:</span>
                  <span style={styles.statValue}>
                    {reportData.paidOrdersCount}
                  </span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>Total Orders:</span>
                  <span style={styles.statValue}>{reportData.totalOrders}</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>Refunded:</span>
                  <span style={{ ...styles.statValue, color: "#DC2626" }}>
                    GHC{formatCurrency(reportData.totalRefunded)}
                  </span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>Voided:</span>
                  <span style={{ ...styles.statValue, color: "#DC2626" }}>
                    GHC{formatCurrency(reportData.totalVoided)}
                  </span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>Total Discount:</span>
                  <span style={{ ...styles.statValue, color: "#D97706" }}>
                    GHC{formatCurrency(reportData.totalDiscount)}
                  </span>
                </div>
              </div>
            </div>

            {/* Cashier Summary Section */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Cashier Summary</h3>
              {cashierSummary.length > 0 ? (
                <div style={styles.cashierSummary}>
                  <div style={styles.cashierHeader}>
                    <span>Cashier</span>
                    <span>Orders</span>
                    <span>Total Sales</span>
                    <span>Revenue</span>
                    <span>Cash</span>
                  </div>
                  {cashierSummary.map((cashier, index) => (
                    <div key={index} style={styles.cashierItem}>
                      <span style={styles.cashierName}>{cashier.name}</span>
                      <span>{cashier.orderCount}</span>
                      <span>GHC{formatCurrency(cashier.totalSales)}</span>
                      <span>GHC{formatCurrency(cashier.totalRevenue)}</span>
                      <span>GHC{formatCurrency(cashier.cashTotal)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={styles.noDataMessage}>
                  No cashier data available
                </div>
              )}
            </div>

            {/* Financial Summary */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Financial Summary</h3>
              <div style={styles.financialGrid}>
                <div style={styles.financialItem}>
                  <span>Grand Total:</span>
                  <span style={styles.amount}>
                    GHC{formatCurrency(reportData.grandTotal)}
                  </span>
                </div>
                <div style={styles.financialItem}>
                  <span>Sub Total:</span>
                  <span>GHC{formatCurrency(reportData.subTotal)}</span>
                </div>
                <div style={styles.financialItem}>
                  <span>Discount:</span>
                  <span style={{ color: "#DC2626" }}>
                    -GHC{formatCurrency(reportData.discount)}
                  </span>
                </div>
                <div style={styles.financialItem}>
                  <span>Service:</span>
                  <span>GHC{formatCurrency(reportData.service)}</span>
                </div>
                <div style={styles.financialItem}>
                  <span>15% VAT:</span>
                  <span>GHC{formatCurrency(reportData.vat)}</span>
                </div>
                <div style={styles.financialItem}>
                  <span>2.5% NHIL:</span>
                  <span>GHC{formatCurrency(reportData.nhil)}</span>
                </div>
                <div style={styles.financialItem}>
                  <span>2.5% GetFund:</span>
                  <span>GHC{formatCurrency(reportData.getfund)}</span>
                </div>
                <div style={styles.financialItem}>
                  <span>Tourism Levy:</span>
                  <span>GHC{formatCurrency(reportData.tourismLevy)}</span>
                </div>
              </div>
            </div>

            {/* Net Totals */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Net Totals</h3>
              <div style={styles.netTotals}>
                <div style={styles.netItem}>
                  <span>Net Sales Without Taxes:</span>
                  <span style={styles.amount}>
                    GHC{formatCurrency(reportData.netSalesWithoutTaxes)}
                  </span>
                </div>
                <div style={styles.netItem}>
                  <span>Net Total:</span>
                  <span style={styles.amountLarge}>
                    GHC{formatCurrency(reportData.netTotal)}
                  </span>
                </div>
                <div style={styles.netItem}>
                  <span>Average Customer:</span>
                  <span>GHC{formatCurrency(reportData.averageCustomer)}</span>
                </div>
                <div style={styles.netItem}>
                  <span>Average Check:</span>
                  <span>GHC{formatCurrency(reportData.averageCheck)}</span>
                </div>
                <div style={styles.netItem}>
                  <span>No Sales #:</span>
                  <span>{reportData.noSales}</span>
                </div>
              </div>
            </div>

            {/* Payment Summary */}
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Summary of Payment</h3>
              <div style={styles.paymentGrid}>
                <div style={styles.paymentItem}>
                  <span>Cash:</span>
                  <span>
                    GHC{formatCurrency(reportData.paymentSummary.cash)}
                  </span>
                </div>
                <div style={styles.paymentItem}>
                  <span>Card:</span>
                  <span>
                    GHC{formatCurrency(reportData.paymentSummary.card)}
                  </span>
                </div>
                <div style={styles.paymentItem}>
                  <span>Mobile Money:</span>
                  <span>
                    GHC{formatCurrency(reportData.paymentSummary.mobile_money)}
                  </span>
                </div>
                <div style={styles.paymentItem}>
                  <span>Bank Transfer:</span>
                  <span>
                    GHC{formatCurrency(reportData.paymentSummary.bank_transfer)}
                  </span>
                </div>
                <div style={styles.paymentTotal}>
                  <span>Total:</span>
                  <span style={styles.amount}>
                    GHC{formatCurrency(reportData.paymentSummary.total)}
                  </span>
                </div>
              </div>
            </div>

            {/* Net Sales Total */}
            <div style={styles.netSalesTotal}>
              <span>Net Sales</span>
              <span style={styles.netSalesAmount}>
                GHC{formatCurrency(reportData.netSales)}
              </span>
            </div>
          </div>
        </div>
      </div>
      <DashboardContentFooter />

      {modalError && (
        <MessageModal
          leftIcon={errorMessage.includes("✅") ? faCheckCircle : faTimes}
          color={errorMessage.includes("✅") ? "green" : "red"}
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f5f7fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "24px 32px 4rem 32px",
  },
  loadingContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "20px",
  },
  loadingText: {
    fontSize: "22px",
    color: "#6B7280",
    fontWeight: "500",
  },
  errorContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
    color: "#d32f2f",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "16px",
  },
  errorTitle: {
    fontSize: "24px",
    fontWeight: "700",
    margin: 0,
  },
  errorSubtitle: {
    fontSize: "18px",
    color: "#6B7280",
    margin: 0,
  },
  retryButton: {
    marginTop: "20px",
    padding: "14px 28px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "18px",
    fontWeight: "600",
    minHeight: "56px",
  },
  headerSection: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1F2937",
    margin: 0,
  },
  subtitle: {
    fontSize: "18px",
    color: "#6B7280",
    margin: "4px 0 0 0",
  },
  filterControls: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  dateWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  dateLabel: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#4B5563",
  },
  dateInput: {
    border: "none",
    fontSize: "20px",
    fontWeight: "600",
    color: "#1F2937",
    outline: "none",
    backgroundColor: "transparent",
    padding: "6px",
  },
  printButton: {
    padding: "12px 20px",
    backgroundColor: "#000000",
    color: "white",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "600",
    minHeight: "48px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  thermalPrintButton: {
    padding: "12px 16px",
    backgroundColor: "#000000",
    color: "white",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    fontSize: "15px",
    fontWeight: "600",
    minHeight: "48px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  reportContent: {
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "40px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "16px",
    marginBottom: "24px",
    padding: "20px",
    backgroundColor: "#F8FAFC",
    borderRadius: "12px",
  },
  summaryItem: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    fontSize: "16px",
    alignItems: "center",
  },
  summaryLabel: {
    color: "#6B7280",
    fontSize: "16px",
    fontWeight: "500",
  },
  summaryValue: {
    fontWeight: "600",
    color: "#1F2937",
    fontSize: "16px",
  },
  section: {
    marginBottom: "28px",
  },
  sectionTitle: {
    fontSize: "20px",
    fontWeight: "700",
    color: "#1F2937",
    borderBottom: "2px solid #E5E7EB",
    paddingBottom: "12px",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
  },
  cashRegisterStats: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "12px 24px",
    padding: "20px",
    backgroundColor: "#F0FDF4",
    borderRadius: "12px",
    border: "2px solid #86EFAC",
  },
  statItem: {
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 0",
    fontSize: "16px",
    alignItems: "center",
  },
  statLabel: {
    color: "#065F46",
    fontWeight: "500",
    fontSize: "16px",
  },
  statValue: {
    fontWeight: "600",
    color: "#1F2937",
    fontSize: "16px",
  },
  cashierSummary: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    padding: "16px 20px",
    background: "#EFF6FF",
    borderRadius: "12px",
    border: "2px solid #93C5FD",
  },
  cashierHeader: {
    display: "grid",
    gridTemplateColumns: "1fr 0.5fr 1fr 1fr 0.8fr",
    gap: "8px",
    padding: "8px 12px",
    fontWeight: "700",
    color: "#1F2937",
    borderBottom: "2px solid #93C5FD",
    fontSize: "15px",
  },
  cashierItem: {
    display: "grid",
    gridTemplateColumns: "1fr 0.5fr 1fr 1fr 0.8fr",
    gap: "8px",
    padding: "8px 12px",
    backgroundColor: "white",
    borderRadius: "6px",
    border: "1px solid #E5E7EB",
    fontSize: "14px",
    alignItems: "center",
  },
  cashierName: {
    fontWeight: "700",
    color: "#1F2937",
  },
  noDataMessage: {
    textAlign: "center",
    padding: "20px",
    color: "#6B7280",
    fontSize: "16px",
  },
  financialGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "10px 24px",
  },
  financialItem: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    fontSize: "16px",
    borderBottom: "1px solid #F3F4F6",
    alignItems: "center",
  },
  netTotals: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "10px 24px",
  },
  netItem: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    fontSize: "16px",
    alignItems: "center",
  },
  paymentGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "10px 24px",
    padding: "16px",
    backgroundColor: "#F8FAFC",
    borderRadius: "12px",
  },
  paymentItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "6px 0",
    fontSize: "16px",
  },
  paymentTotal: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 0",
    fontSize: "18px",
    fontWeight: "700",
    borderTop: "2px solid #E5E7EB",
    marginTop: "6px",
    gridColumn: "1 / -1",
  },
  netSalesTotal: {
    display: "flex",
    justifyContent: "space-between",
    padding: "20px",
    backgroundColor: "#FEF3C7",
    borderRadius: "12px",
    fontSize: "22px",
    fontWeight: "700",
    marginTop: "16px",
    border: "2px solid #F59E0B",
  },
  netSalesAmount: {
    color: "#FF6B35",
    fontSize: "28px",
  },
  amount: {
    fontWeight: "700",
    color: "#1F2937",
    fontSize: "16px",
  },
  amountLarge: {
    fontWeight: "700",
    color: "#FF6B35",
    fontSize: "20px",
  },
};
